// A IDADE, E O QUE A PLATAFORMA FAZ COM ELA.
//
// T1.2 do pedido, marcado CRÍTICO e "requisito obrigatório antes de qualquer uso real":
// "a ferramenta será usada por menores e não tem nenhuma restrição de idade nem trava de
// segurança".
//
// São duas exigências, e elas se confundem com facilidade. A "trava de segurança" é a barreira
// de conteúdo, que já existe e vale para todo mundo igual. A "restrição de idade" é este arquivo:
// perguntar, conferir, e agir.
//
// ── O MECANISMO É AUTODECLARADO, POR DECISÃO DO OPERADOR ──────────────────────────────────────
//
// Ele decidiu: autodeclarado, sem verificação documental. Isso não é frouxidão — verificação de
// identidade de criança é ela mesma um risco de privacidade, e uma escola já sabe quem são seus
// alunos. O que o autodeclarado exige em troca é que a declaração seja CONSCIENTE: a data já era
// pedida no cadastro e ninguém nunca a olhava, o que é o mesmo que não perguntar.
//
// ── O PISO É 6 ANOS, E ESSE NÚMERO NÃO É MEU ─────────────────────────────────────────────────
//
// Seis é a idade de entrada no ensino fundamental brasileiro. É a âncora mais defensável que eu
// tinha: abaixo dela a pessoa não está na escola que usa esta plataforma, e uma conta própria
// nesse caso é quase certamente erro de digitação ou uma conta criada por outra pessoa.
//
// EU NÃO FIXEI UM PISO MAIOR DE PROPÓSITO. Barrar quem tem 9 anos de uma plataforma escolar feita
// para crianças de 9 anos seria cumprir a letra do ponto traindo o produto. Se o operador quiser
// um piso diferente — 13, como em várias plataformas, com conta criada pela professora abaixo
// disso — é decisão dele, e muda só a constante abaixo.

/** O piso etário para conta própria. Ver o comentário acima antes de mexer. */
export const IDADE_MINIMA = 6;

/** Acima disto a data é certamente erro de digitação, não uma pessoa. */
export const IDADE_MAXIMA = 120;

export type FaixaEtaria = "crianca" | "adolescente" | "adulto";

/**
 * Idade em anos completos na data de hoje.
 *
 * O cálculo é por comparação de mês e dia, e não por divisão de milissegundos: dividir por
 * 365,25 erra em quem faz aniversário hoje, que é justamente o caso que alguém vai reclamar.
 */
export function idadeEmAnos(nascimento: string, hoje = new Date()): number | null {
  if (!nascimento) return null;
  const d = new Date(nascimento + "T00:00:00");
  if (Number.isNaN(d.getTime())) return null;

  let anos = hoje.getFullYear() - d.getFullYear();
  const mes = hoje.getMonth() - d.getMonth();
  if (mes < 0 || (mes === 0 && hoje.getDate() < d.getDate())) anos--;
  return anos;
}

export function faixaEtaria(nascimento: string): FaixaEtaria | null {
  const anos = idadeEmAnos(nascimento);
  if (anos === null) return null;
  if (anos < 13) return "crianca";
  if (anos < 18) return "adolescente";
  return "adulto";
}

/**
 * O veredito do portão, em português, pronto para a tela.
 *
 * Devolve `null` quando está tudo certo. A mensagem, quando existe, diz o que fazer — e não só
 * o que está errado. "Data inválida" não ajuda ninguém a seguir em frente.
 */
export function motivoDeRecusa(nascimento: string, hoje = new Date()): string | null {
  // `hoje` entra como parametro para o portao poder ser provado contra data fixa. Sem isso, um
  // teste de limite etario passa hoje e falha no aniversario seguinte de quem o escreveu.
  const anos = idadeEmAnos(nascimento, hoje);
  if (anos === null) return "Preencha a sua data de nascimento.";

  if (anos < 0) {
    return "Essa data ainda não chegou. Confira o ano.";
  }
  if (anos > IDADE_MAXIMA) {
    return "Confira o ano de nascimento — parece que faltou um dígito.";
  }
  if (anos < IDADE_MINIMA) {
    return (
      `Para ter uma conta própria é preciso ter ${IDADE_MINIMA} anos ou mais. ` +
      "Peça para a sua professora criar a sua conta."
    );
  }
  return null;
}

/** "11 anos" / "1 ano" — a idade escrita como a pessoa a leria em voz alta. */
export function idadePorExtenso(nascimento: string): string | null {
  const anos = idadeEmAnos(nascimento);
  if (anos === null || anos < 0) return null;
  return anos === 1 ? "1 ano" : `${anos} anos`;
}
